<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, KeyRound, Mail } from 'lucide-vue-next';

interface Props {
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    canResetPassword?: boolean;
}

withDefaults(defineProps<Props>(), {
    label: 'Portal access',
    loading: false,
    disabled: false,
    canResetPassword: false,
});

const emit = defineEmits<{
    (event: 'invite'): void;
    (event: 'password-setup'): void;
    (event: 'password-reset'): void;
}>();
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button variant="outline" size="sm" class="rounded-full" :disabled="disabled || loading">
                {{ loading ? 'Sending...' : label }}
                <ChevronDown class="ml-2 size-3.5" />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-80">
            <DropdownMenuItem class="items-start gap-3 py-3" @select="emit('invite')">
                <Mail class="mt-0.5 size-4 shrink-0" />
                <div>
                    <p class="font-medium">Send invitation</p>
                    <p class="mt-0.5 text-xs leading-5 text-muted-foreground">The contact accepts the invite and creates or connects their account.</p>
                </div>
            </DropdownMenuItem>
            <DropdownMenuItem class="items-start gap-3 py-3" @select="emit('password-setup')">
                <KeyRound class="mt-0.5 size-4 shrink-0" />
                <div>
                    <p class="font-medium">Grant access and send password setup</p>
                    <p class="mt-0.5 text-xs leading-5 text-muted-foreground">Access is granted now and the contact receives a secure one-time password link.</p>
                </div>
            </DropdownMenuItem>
            <DropdownMenuItem v-if="canResetPassword" class="items-start gap-3 py-3" @select="emit('password-reset')">
                <KeyRound class="mt-0.5 size-4 shrink-0" />
                <div>
                    <p class="font-medium">Send password reset</p>
                    <p class="mt-0.5 text-xs leading-5 text-muted-foreground">Send a fresh secure reset link without changing this contact’s portal access.</p>
                </div>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
